import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { api } from '@/api';
import { PaginationSearchParams, SearchQuery, SortOrderQuery } from '@/api/types';
import { PaginatedLogisticProviderListFilters } from '@/api/apis/facilities';
import { queryClient } from '@/queries';
import { txCenterProvidersQueryKeys } from '@/queries/txcenter-providers/txcenter-providers.keys';

const allLogisticProvidersPaginationParams: PaginationSearchParams = {
  itemsPerPage: 100,
  pageIndex: 0,
};

export const getTxCenterProvidersByIds = async (providersList: string[]) => {
  const providersIdsSet = new Set(providersList);
  const providersIds = Array.from(providersIdsSet);

  if (providersIds) {
    return api.txcenterprovider.getTXCenterProvidersList({
      ids: providersIds.join(','),
      pageIndex: 0,
      itemsPerPage: providersIds.length,
    });
  }

  return null;
};

export const txCenterProvidersQueries = {
  all: () =>
    queryOptions({
      queryKey: txCenterProvidersQueryKeys.getList(allLogisticProvidersPaginationParams),
      queryFn: () => api.txcenterprovider.getTXCenterProvidersList(allLogisticProvidersPaginationParams),
    }),
  list: (listQuery: PaginatedLogisticProviderListFilters) =>
    queryOptions({
      queryKey: txCenterProvidersQueryKeys.getList(listQuery),
      queryFn: () => api.txcenterprovider.getTXCenterProvidersList(listQuery),
      placeholderData: keepPreviousData,
    }),
  infiniteList: (searchPagination: PaginatedLogisticProviderListFilters) =>
    infiniteQueryOptions({
      queryKey: txCenterProvidersQueryKeys.getInfiniteList(searchPagination),
      queryFn: ({ pageParam: pageIndex }) =>
        api.txcenterprovider.getTXCenterProvidersList({ ...searchPagination, pageIndex }),
      initialPageParam: 0,
      placeholderData: keepPreviousData,
      getNextPageParam: (lastPage) => {
        if (lastPage.pageIndex >= lastPage.totalPages) {
          return undefined;
        }

        return lastPage.pageIndex + 1;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage.pageIndex <= 1) {
          return undefined;
        }

        return firstPage.pageIndex - 1;
      },
    }),
  item: (txCenterId: string) =>
    queryOptions({
      queryKey: txCenterProvidersQueryKeys.getItem(txCenterId),
      queryFn: () => api.txcenterprovider.getTXCenterProvidersById(txCenterId),
    }),
};

const useAllTXCenterProvidersListQuery = () => {
  return useQuery(txCenterProvidersQueries.all());
};

const useInfiniteTXCentersProvidersListQuery = (filters: SearchQuery & SortOrderQuery) => {
  return useInfiniteQuery(txCenterProvidersQueries.infiniteList({ itemsPerPage: 10, pageIndex: 0, ...filters }));
};

const useTXCenterProvidersListQuery = (listQuery: PaginatedLogisticProviderListFilters) => {
  return useQuery(txCenterProvidersQueries.list(listQuery));
};

const useTXCenterProviderByIdQuery = (id: string) => {
  return useQuery({
    ...txCenterProvidersQueries.item(id),
    staleTime: 60 * 1000,
    initialData: () => {
      const query = queryClient.getQueryData(txCenterProvidersQueries.all().queryKey);
      return query?.items.find((c: { id: string }) => c.id === id);
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(txCenterProvidersQueries.all().queryKey)?.dataUpdatedAt,
    placeholderData: keepPreviousData,
  });
};

export {
  useAllTXCenterProvidersListQuery,
  useInfiniteTXCentersProvidersListQuery,
  useTXCenterProvidersListQuery,
  useTXCenterProviderByIdQuery,
};
